import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Play, Plus, Square, Trash2 } from 'lucide-react';
import { EXP_DEFAULT_TAU, requiredSamplePerArm } from '@dxp/core';
import {
  createExperiment,
  getExperimentResults,
  getExperiments,
  getGoals,
  getOverview,
  lastNDaysRange,
  qk,
  setExperimentStatus,
  type Experiment,
  type ExperimentVariantDef,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import { DurumMetni, ResultsTable, STATUS_META } from '../components/DeneySonuc.js';
import DeneyTarihleri from '../components/DeneyTarihleri.js';
import { useActivePid } from '../components/Layout.js';
import { formatNumber } from '../lib/format.js';

/**
 * Faz 6 §65.1 — Deneyler sayfası.
 *
 * DEĞİŞMEZ KURAL (README): motor `concluded` demeden bu sayfada "kazanan/önde"
 * KELİMESİ GEÇMEZ. Sürerken yalnız kol sayıları + always-valid aralık gösterilir;
 * durum metni üç değerden biridir: "Veri toplanıyor" / "Sürüyor — henüz sonuç yok" /
 * "Sonuçlandı: …".
 *
 * Sonuç parçaları (`STATUS_META`, `DurumMetni`, `ResultsTable`) `components/DeneySonuc`
 * altındadır: detay sayfası (§102.2) aynılarını çizer, kural iki kopyada tutulamaz.
 */

function ExperimentCard({ experiment }: { experiment: Experiment }) {
  const client = useQueryClient();
  const sonuc = useQuery({
    queryKey: qk.experimentResults(experiment.id),
    queryFn: ({ signal }) => getExperimentResults(experiment.id, signal),
    enabled: experiment.status !== 'draft',
  });
  const durum = useMutation({
    mutationFn: (status: 'running' | 'stopped') => setExperimentStatus(experiment.id, status),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.experiments(experiment.pid) });
      void client.invalidateQueries({ queryKey: qk.experimentResults(experiment.id) });
    },
  });
  const meta = STATUS_META[experiment.status];

  return (
    <div data-testid="deney-satiri">
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {/* §102.2 — deneyin kendi adresi: paylaşılabilir, yer imlenebilir. */}
            <Link
              to={`/deneyler/${experiment.id}`}
              data-testid="deney-detay-link"
              className="text-[15px] font-semibold text-[#111827] underline decoration-[#d1d5db] underline-offset-4 hover:text-brand hover:decoration-brand"
            >
              {experiment.name}
            </Link>
            <Badge tone={meta.tone}>{meta.label}</Badge>
          </div>
          {experiment.hypothesis ? (
            <p className="mt-1 text-[13px] text-[#6b7280]">{experiment.hypothesis}</p>
          ) : null}
          <p className="mt-1 text-[12px] text-[#9ca3af]">
            {experiment.variants
              .map((v) =>
                v.label === undefined || v.label === ''
                  ? `${v.key} %${Math.round(v.weight * 100)}`
                  : `${v.key} %${Math.round(v.weight * 100)} → ${v.label}`,
              )
              .join(' · ')}
          </p>
          {/*
            §107.3 — kartta da "hangi sayfa": listeye bakan kişinin ilk sorusu bu.
            §109.4 — yüzey İKİ biçimde olabilir; split-URL deneyinde `targetPattern`
            tanım gereği NULL'dır ve yüzey VARYANT SAYFALARIDIR. Bu ayrım detay
            sayfasında düzeltilmişti ama kartta ikinci bir kopya olarak kalmıştı:
            doğru kurulmuş bir split deney listede "tanımsız" görünüyordu.
          */}
          <p className="mt-0.5 text-[12px] text-[#9ca3af]" data-testid="deney-yuzey-etiketi">
            Yüzey:{' '}
            {experiment.targetPattern ??
              (experiment.variants.some((v) => v.url !== undefined)
                ? experiment.variants.map((v) => v.url).filter(Boolean).join(' / ')
                : 'tanımsız')}
          </p>
          {/* §65.1 listede "başlangıç" ister; §102.3 sözleşmesi onu tarih ailesiyle verir. */}
          <DeneyTarihleri experiment={experiment} compact />
        </div>
        <div className="flex items-center gap-2">
          {/*
            AÇIK ULAŞIM YOLU. Detay sayfasına giden tek bağlantı deney ADIYDI ve
            adın stili düz başlıkla BİREBİR AYNIYDI: `text-decoration: none`,
            gövde rengi. Yani yol mekanik olarak vardı (`dead-surface-check`
            geçiyordu) ama kullanıcı için YOKTU — Faz 7'nin "ölü yüzey, bağlanmamış
            özelliktir" dersinin bir basamak inceltilmiş hâli: soru "ulaşan bir yol
            var mı" değil, "kullanıcının GÖREBİLECEĞİ bir yol var mı" olmalı.
          */}
          <Link
            to={`/deneyler/${experiment.id}`}
            data-testid="deney-detay-dugmesi"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] px-3 py-1.5 text-[13px] font-medium text-[#374151] hover:border-[#c8c8c8]"
          >
            Detay <ArrowRight size={14} />
          </Link>
          {experiment.status === 'draft' ? (
            <button
              type="button"
              data-testid="deney-baslat"
              onClick={() => durum.mutate('running')}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white"
            >
              <Play size={14} /> Başlat
            </button>
          ) : null}
          {experiment.status === 'running' ? (
            <button
              type="button"
              data-testid="deney-durdur"
              onClick={() => durum.mutate('stopped')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] px-3 py-1.5 text-[13px] font-medium text-[#374151]"
            >
              <Square size={14} /> Durdur
            </button>
          ) : null}
        </div>
      </div>

      {experiment.status !== 'draft' ? (
        <div className="mt-4 space-y-2" data-testid="deney-sonuc">
          {sonuc.isLoading ? (
            <LoadingBlock />
          ) : sonuc.isError ? (
            <ErrorState description={String(sonuc.error)} />
          ) : (
            <>
              <DurumMetni experiment={experiment} results={sonuc.data?.results ?? []} />
              <ResultsTable experiment={experiment} results={sonuc.data?.results ?? []} />
            </>
          )}
        </div>
      ) : null}
    </Card>
    </div>
  );
}

/**
 * §64.4 — BEKLENEN ÖRNEKLEM (planlama aracı, karar mekanizması DEĞİL).
 *
 * Sayı `@dxp/core#requiredSamplePerArm`tan gelir; panel formülü KOPYALAMAZ. Motorun
 * kullandığı z sabitleri ve `τ = MDE` seçimi orada tanımlıdır — burada yeniden yazmak,
 * bir gün ayrışıp panelin operatöre motorun beklemediği bir hedef göstermesi demekti
 * (SigV4/OIDC'de tutulan "tek gerçek kaynak" disiplininin aynısı).
 *
 * DURDURMA KURALI DEĞİLDİR: deney bu sayıya ulaşınca bitmez; mSPRT daha erken de daha
 * geç de sonuç verebilir (§64.3). Metin bunu açıkça söyler, yoksa operatör sayıyı bir
 * söz sanır ve "neden hâlâ bitmedi" diye sorar.
 */
function OrneklemTahmini({ pid, variantCount }: { pid: string; variantCount: number }) {
  const { from, to } = lastNDaysRange(30);
  const overview = useQuery({
    queryKey: qk.overview(pid, from, to),
    queryFn: ({ signal }) => getOverview(pid, from, to, signal),
  });

  if (overview.isLoading) return <p className="text-[12px] text-[#9ca3af]">Site CR’si okunuyor…</p>;

  const cr = overview.data?.cr ?? 0;
  const kolBasina = requiredSamplePerArm(cr, EXP_DEFAULT_TAU);

  if (kolBasina === null) {
    /*
     * Dejenere girdi (CR 0 ya da 1): tahmin ÜRETİLMEZ. Uydurma bir sayı, planlamanın
     * tek işlevi olan "beklenti yönetimi"ni tersine çevirirdi.
     */
    return (
      <p data-testid="deney-orneklem" className="text-[12px] text-[#9ca3af]">
        Beklenen örneklem hesaplanamıyor — son 30 günde ölçülmüş bir dönüşüm oranı yok.
      </p>
    );
  }

  return (
    <p data-testid="deney-orneklem" className="text-[12px] text-[#6b7280]">
      Beklenen örneklem: <strong>kol başına ~{formatNumber(kolBasina)} oturum</strong> (toplam ~
      {formatNumber(kolBasina * variantCount)}); site CR’si %
      {(cr * 100).toFixed(1).replace('.', ',')}, aranan fark {EXP_DEFAULT_TAU * 100} puan, güç %80.{' '}
      <span className="text-[#9ca3af]">
        Bu bir DURDURMA kuralı değildir — deney always-valid teste göre daha erken de daha
        geç de sonuçlanabilir (§64.3).
      </span>
    </p>
  );
}

/** §62.1 — sunucunun varyant key sözleşmesi; panel AYNI kuralı önden uygular. */
const VARIANT_KEY_RE = /^[a-z0-9_-]{1,32}$/;
/** Panel tavanı: sunucu 8'e izin verir, §64.5 Bonferroni bedeli 4'ten sonra ürünü yanıltır. */
const MAX_UI_VARIANTS = 4;

interface VaryantSatiri {
  key: string;
  /** YÜZDE olarak tutulur; gönderimde orana çevrilir (kullanıcı yüzde düşünür). */
  pct: number;
  /** §107.3 KARAR 2 — bu varyant NE gösteriyor ("Sepete Ekle" / "Hemen Al"). */
  label: string;
  /** §109 — split-URL modunda bu varyantın sayfası (YOL). */
  url: string;
}

/**
 * §65.1 — yaratma formu: ad, hipotez, VARYANTLAR (key + ağırlık), hedef.
 *
 * Varyant düzenleyicisi §65.1'in açık isteğidir ve eskiden YOKTU: form yalnız kol
 * SAYISINI alıyor, key'leri `control`/`v1`/`v2` diye kendisi uyduruyor ve ağırlıkları
 * eşit bölüyordu. Bu, ürünün en temel deney kararını (hangi varyant, trafiğin ne
 * kadarı) kullanıcıdan saklıyordu — %90/%10 bir rampalı yayın kurmak imkânsızdı.
 *
 * `control` İLK SATIRDIR ve adı değiştirilemez (§62.1 sunucu doğrulaması); silinemez
 * de — kontrol kolu olmayan bir deneyin karşılaştıracağı taban yoktur.
 */
function YeniDeneyFormu({ pid, onDone }: { pid: string; onDone: () => void }) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [variants, setVariants] = useState<VaryantSatiri[]>([
    { key: 'control', pct: 50, label: '', url: '' },
    { key: 'v1', pct: 50, label: '', url: '' },
  ]);
  /**
   * §109 — SPLIT-URL modu. Ayrı bir şema alanı DEĞİLDİR (KARAR 5): deney
   * split-URL'dir ancak ve ancak varyantları `url` taşıyorsa. Bu anahtar yalnız
   * formun hangi alanları göstereceğini belirler.
   */
  const [splitUrl, setSplitUrl] = useState(false);
  /**
   * §107.3 KARAR 1 — TEST YÜZEYİ: deney hangi sayfada koşuyor.
   *
   * Boş bırakılabilir (site-genel deneyler var) ama bedeli formda YAZILIDIR:
   * yüzeysiz deneyde motor "uygulandı mı" sayımını yapamaz ve panel sonuçların
   * yanında "doğrulanamıyor" der. Bu alan olmadan ürün, hiç uygulanmamış bir
   * deneyi de sonuçlandırıyordu (§107.2).
   */
  const [targetPattern, setTargetPattern] = useState('');
  /** §61.2 KARAR 3 — boş = "aktif hedef"; deney kendi hedefini SEÇMEK ZORUNDA değildir. */
  const [goalId, setGoalId] = useState<number | null>(null);

  const goals = useQuery({
    queryKey: qk.goals(pid),
    queryFn: ({ signal }) => getGoals(pid, signal),
  });

  const toplam = variants.reduce((acc, v) => acc + v.pct, 0);
  const keyHatasi = variants.find((v) => !VARIANT_KEY_RE.test(v.key));
  const tekrarEden = variants.length !== new Set(variants.map((v) => v.key)).size;
  /*
   * Sunucu ağırlık toplamını 1e-6 toleransla sınar; panel ONDAN ÖNCE aynı kuralı
   * uygular ki kullanıcı 400 yerine yerinde bir cümle görsün (§65.1 "bedeli önden
   * söyle" disiplini).
   */
  // §107.5 — sunucu deseni '/' ile başlatır; panel AYNI kuralı önden uygular.
  const yuzeyHatali = targetPattern.trim() !== '' && !targetPattern.trim().startsWith('/');
  /*
   * §109.2 KARAR 4/5 — split modda her varyant bir YOL taşımalı. Eksik ya da
   * mutlak adres, sunucuya gitmeden burada yakalanır: yarım kalan bir kolun
   * yönlendirmesi olmaz ama damgası basılır ve analiz onu "gördü" sayardı.
   */
  const splitHatali =
    splitUrl && variants.some((v) => v.url.trim() === '' || !v.url.trim().startsWith('/'));
  const gecerli =
    name.trim() !== '' &&
    Math.abs(toplam - 100) < 0.01 &&
    keyHatasi === undefined &&
    !tekrarEden &&
    !yuzeyHatali &&
    !splitHatali;

  function guncelle(i: number, alan: Partial<VaryantSatiri>): void {
    setVariants((mevcut) => mevcut.map((v, j) => (j === i ? { ...v, ...alan } : v)));
  }

  function esitBol(): void {
    setVariants((mevcut) => {
      const pay = Math.floor((100 / mevcut.length) * 10) / 10;
      return mevcut.map((v, i) => ({
        ...v,
        // Son satır kalanı alır: 3 kolda 33,3+33,3+33,3 = 99,9 ≠ 100 olurdu.
        pct: i === mevcut.length - 1 ? Math.round((100 - pay * (mevcut.length - 1)) * 10) / 10 : pay,
      }));
    });
  }

  const olustur = useMutation({
    mutationFn: () => {
      const kolSayisi = variants.length;
      const gonderilecek: ExperimentVariantDef[] = variants.map((v, i) => ({
        key: v.key,
        ...(v.label.trim() === '' ? {} : { label: v.label.trim() }),
        ...(splitUrl && v.url.trim() !== '' ? { url: v.url.trim() } : {}),
        // Kayan nokta toplamı 1'den sapmasın: son varyant kalanı alır (§62.2 uyumlu).
        weight:
          i === kolSayisi - 1
            ? 1 - variants.slice(0, -1).reduce((acc, x) => acc + x.pct / 100, 0)
            : v.pct / 100,
      }));
      return createExperiment(pid, {
        name,
        hypothesis,
        variants: gonderilecek,
        goalId,
        targetPattern: targetPattern.trim() === '' ? null : targetPattern.trim(),
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.experiments(pid) });
      onDone();
    },
  });

  return (
    <Card>
      <form
        data-testid="deney-formu"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (gecerli) olustur.mutate();
        }}
      >
        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="deney-ad">
            Deney adı
          </label>
          <input
            id="deney-ad"
            data-testid="deney-ad"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
            placeholder="Checkout CTA testi"
          />
        </div>
        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="deney-hipotez">
            Hipotez (opsiyonel)
          </label>
          <input
            id="deney-hipotez"
            value={hypothesis}
            onChange={(e) => setHypothesis(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
            placeholder="Kısa CTA metni dönüşümü artırır"
          />
        </div>
        {/*
          §107.3 KARAR 1 — TEST YÜZEYİ. Bu alan olmadan ürün, hiç uygulanmamış bir
          deneyi de sonuçlandırıyordu: SDK atama damgasını site `variant()` çağırsın
          ya da çağırmasın basar, yani "deneyi açıp siteye dokunmamak" sessizce
          geçerli bir sonuç üretiyordu (§107.2). Yüzey, motorun "bu deney gerçekten
          uygulanmış mı" sayımını yapabilmesinin TEK girdisidir.
        */}
        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="deney-yuzey">
            Test yüzeyi — hangi sayfada koşuyor? (opsiyonel)
          </label>
          <input
            id="deney-yuzey"
            data-testid="deney-yuzey"
            value={targetPattern}
            onChange={(e) => setTargetPattern(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
            placeholder="/urun/:slug"
          />
          {yuzeyHatali ? (
            <p data-testid="deney-yuzey-hata" className="mt-1 text-[12px] text-red-600">
              Desen "/" ile başlamalı (örn. /urun/:slug).
            </p>
          ) : (
            <p className="mt-1 text-[12px] text-[#9ca3af]">
              {targetPattern.trim() === ''
                ? 'Boş bırakırsan deneyin sitede gerçekten uygulanıp uygulanmadığı DOĞRULANAMAZ — panel sonuçların yanında bunu yazar.'
                : 'Motor, bu deneye atanan oturumların bu sayfayı görüp görmediğini sayar.'}
            </p>
          )}
        </div>

        {/*
          §109 — SPLIT-URL modu. Açıldığında her varyant kendi SAYFASINI alır ve
          yönlendirmeyi müşterinin sunucusu yapar; ürün yönlendirmez (§67).
        */}
        <label className="flex items-start gap-2 text-[13px] text-[#374151]">
          <input
            type="checkbox"
            data-testid="deney-split-url"
            checked={splitUrl}
            onChange={(e) => setSplitUrl(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Bu deney iki ayrı sayfayı karşılaştırıyor (split-URL)
            <span className="block text-[12px] text-[#9ca3af]">
              Her varyant kendi adresini alır. Yönlendirmeyi ÜRÜN YAPMAZ — sunucun yapar ve
              kararı adrese yazar; detay sayfasında tam talimat var.
            </span>
          </span>
        </label>

        {/* §65.1 — varyantlar: key + ağırlık. `control` ilk satırdır, adı sabittir. */}
        <div data-testid="deney-varyantlar">
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-[#6b7280]">Varyantlar (control dahil)</span>
            <button
              type="button"
              data-testid="deney-esit-bol"
              onClick={esitBol}
              className="text-[12px] font-medium text-brand"
            >
              Eşit böl
            </button>
          </div>

          <div className="mt-1 space-y-2">
            {variants.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`Varyant ${i + 1} adı`}
                  data-testid={`deney-varyant-key-${i}`}
                  value={v.key}
                  // §62.1 — control'ün adı sunucuda zorunludur; düzenlenebilir
                  // göstermek, kullanıcıya olmayan bir seçenek sunmak olurdu.
                  readOnly={i === 0}
                  onChange={(e) => guncelle(i, { key: e.target.value })}
                  className={`w-40 rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px] ${
                    i === 0 ? 'bg-[#f9fafb] text-[#6b7280]' : ''
                  }`}
                />
                <div className="flex items-center gap-1">
                  <input
                    aria-label={`Varyant ${i + 1} ağırlığı`}
                    data-testid={`deney-varyant-pct-${i}`}
                    type="number"
                    min={0.1}
                    max={100}
                    step={0.1}
                    value={v.pct}
                    onChange={(e) => guncelle(i, { pct: Number(e.target.value) })}
                    className="num w-24 rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
                  />
                  <span className="text-[13px] text-[#6b7280]">%</span>
                </div>
                {splitUrl ? (
                  <input
                    aria-label={`Varyant ${i + 1} sayfası`}
                    data-testid={`deney-varyant-url-${i}`}
                    value={v.url}
                    onChange={(e) => guncelle(i, { url: e.target.value })}
                    placeholder="/kampanya-a"
                    className="min-w-[160px] flex-1 rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
                  />
                ) : null}
                <input
                  aria-label={`Varyant ${i + 1} ne gösteriyor`}
                  data-testid={`deney-varyant-label-${i}`}
                  value={v.label}
                  onChange={(e) => guncelle(i, { label: e.target.value })}
                  placeholder="ne gösteriyor? (ör. Hemen Al)"
                  className="min-w-[180px] flex-1 rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
                />
                {i > 0 ? (
                  <button
                    type="button"
                    aria-label={`Varyant ${i + 1} sil`}
                    data-testid={`deney-varyant-sil-${i}`}
                    onClick={() => setVariants((m) => m.filter((_, j) => j !== i))}
                    className="rounded-lg border border-[#e5e7eb] p-2 text-[#6b7280]"
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          {variants.length < MAX_UI_VARIANTS ? (
            <button
              type="button"
              data-testid="deney-varyant-ekle"
              onClick={() =>
                setVariants((m) => [...m, { key: `v${m.length}`, pct: 0, label: '', url: '' }])
              }
              className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-brand"
            >
              <Plus size={13} /> Varyant ekle
            </button>
          ) : null}

          {/* Toplam ağırlık: sunucu 400 vermeden ÖNCE panelde söylenir. */}
          <p
            data-testid="deney-agirlik-toplam"
            className={`mt-2 text-[12px] ${
              Math.abs(toplam - 100) < 0.01 ? 'text-[#9ca3af]' : 'text-red-600'
            }`}
          >
            Toplam ağırlık: %{formatNumber(toplam, 1)}
            {Math.abs(toplam - 100) < 0.01 ? '' : ' — %100 olmalı'}
          </p>
          {keyHatasi !== undefined ? (
            <p data-testid="deney-varyant-hata" className="text-[12px] text-red-600">
              Varyant adı yalnız küçük harf, rakam, "-" ve "_" içerebilir (en çok 32 karakter).
            </p>
          ) : null}
          {tekrarEden ? (
            <p data-testid="deney-varyant-tekrar" className="text-[12px] text-red-600">
              Varyant adları benzersiz olmalı.
            </p>
          ) : null}
          {splitHatali ? (
            <p data-testid="deney-split-hata" className="text-[12px] text-red-600">
              Split-URL'de HER varyant bir sayfa yolu taşımalı ("/" ile başlar). Mutlak adres
              kabul edilmez.
            </p>
          ) : null}

          {/* §64.5 — üçten çok kolda Bonferroni α'yı böler; bedeli ÖNDEN söylenir. */}
          {variants.length > 2 ? (
            <p className="mt-1 text-[12px] text-[#9ca3af]">
              İkiden çok kolda anlamlılık eşiği Bonferroni ile bölünür (α/{variants.length - 1}) —
              sonuç daha geç gelir.
            </p>
          ) : null}
        </div>

        {/*
          §65.1 — hedef seçici. Varsayılan "aktif hedef"tir (§61.2 KARAR 3): deney
          hedefi §11.5 keşfinin ÜSTÜNE gelir, yerine geçmez. Boş bırakmak, hedefi
          proje neyi ölçüyorsa ona bağlar; sabitlemek ise deneyi o hedefe kilitler
          (running deneyde DEĞİŞTİRİLEMEZ — §62.1).
        */}
        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="deney-hedef">
            Hedef
          </label>
          <select
            id="deney-hedef"
            data-testid="deney-hedef"
            value={goalId === null ? '' : String(goalId)}
            onChange={(e) => setGoalId(e.target.value === '' ? null : Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-[14px]"
          >
            <option value="">Aktif hedef (otomatik)</option>
            {(goals.data ?? [])
              .filter((g) => g.status !== 'rejected')
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.url_pattern}
                  {g.status === 'confirmed' ? ' — onaylı' : ' — aday'}
                </option>
              ))}
          </select>
        </div>

        <OrneklemTahmini pid={pid} variantCount={variants.length} />

        {olustur.isError ? <ErrorState description={String(olustur.error)} /> : null}
        <button
          type="submit"
          data-testid="deney-olustur"
          disabled={olustur.isPending || !gecerli}
          className="rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50"
        >
          Deney oluştur (taslak)
        </button>
      </form>
    </Card>
  );
}

export default function Deneyler() {
  const pid = useActivePid();
  const [formAcik, setFormAcik] = useState(false);
  const deneyler = useQuery({
    queryKey: qk.experiments(pid),
    queryFn: ({ signal }) => getExperiments(pid, signal),
  });

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="deneyler-sayfasi">
      <div className="border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[18px] font-semibold text-[#111827]">Deneyler</h1>
              <BilgiDugmesi testId="deneyler-bilgi">A/B deneylerini buradan açar ve izlersiniz: varyantları SDK ziyaretçilere otomatik dağıtır, sonuçlar always-valid sıralı testle sürekli izlenir (§18). "Kazanan" ilanını siz değil istatistik motoru verir — sonuca erken bakmak testi bozmaz.</BilgiDugmesi>
            </div>
            <p className="text-[13px] text-[#6b7280]">
              A/B deneyleri — always-valid sıralı test; kazanan ilanını istatistik motoru verir.
            </p>
          </div>
          <button
            type="button"
            data-testid="deney-yeni"
            onClick={() => setFormAcik((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] px-3 py-1.5 text-[13px] font-medium text-[#374151]"
          >
            <FlaskConical size={14} /> Yeni deney
          </button>
        </div>
      </div>

      <div className="space-y-6 px-6 py-8">
        {formAcik ? <YeniDeneyFormu pid={pid} onDone={() => setFormAcik(false)} /> : null}

        {deneyler.isLoading ? (
          <LoadingBlock />
        ) : deneyler.isError ? (
          <ErrorState description={String(deneyler.error)} />
        ) : (deneyler.data ?? []).length === 0 ? (
          <EmptyState
            title="Henüz deney yok"
            description="İlk A/B deneyini oluştur; SDK varyantları deterministik dağıtır, motor sonucu always-valid testle değerlendirir."
          />
        ) : (
          (deneyler.data ?? []).map((exp) => <ExperimentCard key={exp.id} experiment={exp} />)
        )}
      </div>
    </div>
  );
}
